import React, { useState } from 'react';
import { Calculator } from '../../../types';
import { Icon } from '../../Icon';
import { CalculatorShell } from '../CalculatorShell';
import { maskNumber, maskInteger, parseBrazilianNumber } from '../../../utils/formatUtils';

interface FeriasCalculatorProps {
  calculator: Calculator;
  onBack: () => void;
}

const FeriasCalculator = () => {
  const [salary, setSalary] = useState<string>('');
  const [monthsWorked, setMonthsWorked] = useState<string>('');
  const [dependents, setDependents] = useState<string>('');
  const [hasAbono, setHasAbono] = useState<boolean>(true);
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const sal = parseFloat(parseBrazilianNumber(salary)) || 0;
    const months = parseInt(parseBrazilianNumber(monthsWorked)) || 0;
    const deps = parseInt(parseBrazilianNumber(dependents)) || 0;

    if (sal <= 0 || months <= 0) return;

    // Cálculo das férias proporcionais
    const proportionalVacationDays = (months / 12) * 30;
    const vacationValue = (sal / 30) * proportionalVacationDays;

    // Terço constitucional (adicional de férias)
    const thirdValue = vacationValue / 3;

    // Abono pecuniário (vender 1/3 das férias)
    let abonoValue = 0;
    let netVacationValue = 0;

    if (hasAbono) {
      abonoValue = thirdValue;
      netVacationValue = vacationValue; // Recebe só os 2/3
    } else {
      netVacationValue = vacationValue + thirdValue; // Recebe férias completas
    }

    // Cálculo do INSS sobre férias (se houver conversão em abono)
    let inssDiscount = 0;
    if (hasAbono) {
      // INSS só incide sobre o abono se houver conversão
      inssDiscount = abonoValue * 0.075; // Alíquota mínima do INSS
    }

    // Cálculo do IRPF sobre o abono
    let irpfDiscount = 0;
    if (hasAbono && abonoValue > 1903.98) { // Dedução mensal 2024
      const baseIR = abonoValue - inssDiscount;
      if (baseIR <= 2112.00) {
        irpfDiscount = 0;
      } else if (baseIR <= 2826.65) {
        irpfDiscount = (baseIR * 0.075) - 158.40;
      } else if (baseIR <= 3751.05) {
        irpfDiscount = (baseIR * 0.15) - 370.40;
      } else if (baseIR <= 4664.68) {
        irpfDiscount = (baseIR * 0.225) - 651.73;
      } else {
        irpfDiscount = (baseIR * 0.275) - 884.96;
      }
      irpfDiscount = Math.max(0, irpfDiscount);
    }

    const totalDiscounts = inssDiscount + irpfDiscount;
    const netAbonoValue = abonoValue - totalDiscounts;

    setResultState({
      salary: sal,
      monthsWorked: months,
      proportionalVacationDays: Math.floor(proportionalVacationDays),
      vacationValue,
      thirdValue,
      netVacationValue,
      abonoValue,
      hasAbono,
      inssDiscount,
      irpfDiscount,
      totalDiscounts,
      netAbonoValue
    });
  };

  const hasResult = resultState !== null;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-yellow-50 to-slate-50 rounded-full border border-yellow-200 mb-4">
          <span className="text-2xl">☀️</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">Férias</span>
        </div>
        <p className="text-theme-muted text-sm">Cálculo completo com terço constitucional e abono pecuniário</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Informações para Cálculo</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Salário (R$)</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-xl font-black text-theme-muted">R$</span>
              <input
                type="text"
                value={salary}
                onChange={e => setSalary(maskNumber(e.target.value))}
                placeholder="Ex: 5.000,00"
                className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400 pl-12"
              />
            </div>
            <div className="text-xs text-theme-muted mt-2">Salário mensal base</div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Meses Trabalhados</label>
            <input
              type="text"
              value={monthsWorked}
              onChange={e => setMonthsWorked(maskInteger(e.target.value))}
              placeholder="Ex: 12"
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            />
            <div className="text-xs text-theme-muted mt-2">Período aquisitivo (máx 12 meses)</div>
          </div>
        </div>

        <div className="mt-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={hasAbono}
              onChange={e => setHasAbono(e.target.checked)}
              className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-theme-text">
              Deseja converter 1/3 em abono pecuniário?
            </span>
          </label>
          <div className="text-xs text-theme-muted mt-1">Se marcado, receberá 2/3 das férias + 1/3 em dinheiro</div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Férias
          </button>
        </div>
      </div>

      {hasResult && (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-yellow-500 via-orange-500 to-amber-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] text-center shadow-2xl border-2 bg-gradient-to-br from-yellow-500 to-orange-500 border-yellow-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">☀️</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Dias de Férias</p>
                <p className="text-3xl font-black">{resultState.proportionalVacationDays} dias</p>
                <p className="text-xs opacity-70 mt-1">proporcionais ao período</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Valor das Férias</p>
                <p className="text-2xl font-black">R$ {resultState.netVacationValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.hasAbono ? '2/3 do valor' : 'valor completo'}</p>
              </div>
            </div>

            {resultState.hasAbono && (
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 mb-8">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Abono Pecuniário</p>
                <p className="text-2xl font-black">R$ {resultState.netAbonoValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">1/3 convertido em dinheiro</p>
                {(resultState.inssDiscount > 0 || resultState.irpfDiscount > 0) && (
                  <div className="text-xs opacity-70 mt-2">
                    Descontos: INSS R$ {resultState.inssDiscount.toFixed(2)} | IRPF R$ {resultState.irpfDiscount.toFixed(2)}
                  </div>
                )}
              </div>
            )}

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">📋 Resumo</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Salário base:</span>
                  <span className="font-bold">R$ {resultState.salary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span>Período trabalhado:</span>
                  <span className="font-bold">{resultState.monthsWorked} meses</span>
                </div>
                <div className="flex justify-between">
                  <span>Terço constitucional:</span>
                  <span className="font-bold">R$ {resultState.thirdValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between border-t pt-1 mt-1">
                  <span>Total a receber:</span>
                  <span className="font-bold text-lg">
                    R$ {(resultState.netVacationValue + (resultState.hasAbono ? resultState.netAbonoValue : 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!hasResult && (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">☀️</div>
          <p className="font-bold text-base">Preencha os dados para calcular as férias</p>
        </div>
      )}
    </div>
  );
};

export default FeriasCalculator;
